import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/free-solid-svg-icons";

import "./Thumbnail.scss";
import useFavorites from "../FavoritesUtils";

const Thumbnail = ({
    video,
    title,
    category,
    instructor,
    date,
    duration,
    image,
    instructorImage,
    tags,
    onClick,
    isFavorite,
    onFavoriteToggle,
    favorites,
    toggleFavorites,
    inSlider = false,
}) => {
    const thumbnailStyle = {
        backgroundImage: `linear-gradient(-90deg, transparent, rgba(0, 0, 0, .7)), url(${image})`,
    };

    //const { favorites, toggleFavorite } = useFavorites(); // Use useFavorites without passing any props

    //const isFavorite = favorites.includes(video.id);
    //const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite); // I think this is dead?

    const handleButtonClicked = (e) => {
        e.stopPropagation();
        onFavoriteToggle(video.id, !isFavorite);
    };

    const formatDate = (dateString) => {
        const options = { year: "numeric", month: "long", day: "numeric" };
        const formattedDate = new Date(dateString).toLocaleDateString(
            undefined,
            options
        );
        return formattedDate;
    };

    // Get up to 3 specific tags to display
    const getDisplayTags = () => {
        if (!tags || !Array.isArray(tags)) return [];
        
        // Define the specific tags we want to display (in order of priority)
        const specificTags = ["All Levels", "Intermediate", "Advanced"];
        
        // Filter and get up to 3 matching tags
        const matchingTags = specificTags.filter(tag => tags.includes(tag));
        
        return matchingTags.slice(0, 3);
    };

    const displayTags = getDisplayTags();

    // Dynamic font sizing for long titles and instructor names
    const titleRef = useRef(null);
    const instructorRef = useRef(null);
    const [titleFontSize, setTitleFontSize] = useState(null);
    const [instructorFontSize, setInstructorFontSize] = useState(null);
    const hasFitted = useRef(false);

    useEffect(() => {
        // Reset on title/instructor change
        hasFitted.current = false;
        setTitleFontSize(null);
        setInstructorFontSize(null);
    }, [title, instructor]);

    useEffect(() => {
        let resizeTimeout;
        let observerTimeout;
        
        const fitElements = (forceReset = false) => {
            // Skip if already fitted and not forcing reset
            if (hasFitted.current && !forceReset) return;
            
            // Fit title (max 3 lines)
            const titleEl = titleRef.current;
            if (titleEl && title) {
                try {
                    if (forceReset) titleEl.style.fontSize = '';
                    
                    let currentSize = parseFloat(getComputedStyle(titleEl).fontSize);
                    const minSize = 11;
                    
                    let iterations = 0;
                    while (titleEl.scrollHeight > titleEl.clientHeight + 1 && currentSize > minSize && iterations < 20) {
                        currentSize -= 1;
                        titleEl.style.fontSize = `${currentSize}px`;
                        iterations++;
                    }
                    
                    if (iterations > 0) setTitleFontSize(currentSize);
                    else if (forceReset) setTitleFontSize(null);
                } catch (e) {}
            }

            // Fit instructor (single line)
            const instructorEl = instructorRef.current;
            if (instructorEl && instructor) {
                try {
                    if (forceReset) instructorEl.style.fontSize = '';
                    
                    const containerWidth = instructorEl.parentElement?.clientWidth || instructorEl.clientWidth;
                    let currentSize = parseFloat(getComputedStyle(instructorEl).fontSize);
                    const minSize = 9;
                    
                    let iterations = 0;
                    while (instructorEl.scrollWidth > containerWidth && currentSize > minSize && iterations < 20) {
                        currentSize -= 0.5;
                        instructorEl.style.fontSize = `${currentSize}px`;
                        iterations++;
                    }
                    
                    if (iterations > 0) setInstructorFontSize(currentSize);
                    else if (forceReset) setInstructorFontSize(null);
                } catch (e) {}
            }

            hasFitted.current = true;
        };

        // Initial check after first paint (non-blocking)
        const timer1 = requestAnimationFrame(() => fitElements(false));
        // Single follow-up for late renders
        const timer2 = setTimeout(() => fitElements(false), 250);

        // Debounced resize handler
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                hasFitted.current = false;
                fitElements(true);
            }, 100);
        };

        window.addEventListener('resize', handleResize, { passive: true });

        // ResizeObserver with debounce for container size changes
        let resizeObserver;
        const containerEl = titleRef.current?.closest('.thumbnail-container');
        if (containerEl && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                if (!hasFitted.current) return;
                clearTimeout(observerTimeout);
                observerTimeout = setTimeout(() => {
                    hasFitted.current = false;
                    fitElements(true);
                }, 100);
            });
            resizeObserver.observe(containerEl);
        }

        return () => {
            cancelAnimationFrame(timer1);
            clearTimeout(timer2);
            clearTimeout(resizeTimeout);
            clearTimeout(observerTimeout);
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
        };
    }, [title, instructor]);

    return (
        <div
            className={`thumbnail-container ${inSlider ? 'in-slider' : ''}`}
            style={thumbnailStyle}
            onClick={onClick}
        >
            <div className="thumbnail-content">
                <h3 className="date-and-duration">
                    {formatDate(date)} | {duration} min
                    <br></br>
                    {category}
                </h3>
                <button
                    className="thumbnail-favorite-button"
                    onClick={handleButtonClicked}
                >
                    <FontAwesomeIcon
                        className="heart-icon"
                        icon={faHeart}
                        color={isFavorite ? "#ae564a" : "rgba(0,0,0,0.5)"}
                        strokeWidth={isFavorite ? 0 : 2}
                    />
                </button>
                <div className="title-and-instructor-container">
                    {displayTags.length > 0 && (
                        <div className="tags-container">
                            {displayTags.map((tag, index) => (
                                <div key={index} className="video-tag">
                                    {tag}
                                </div>
                            ))}
                        </div>
                    )}
                    <h1 
                        ref={titleRef}
                        className="video-title"
                        style={titleFontSize ? { fontSize: `${titleFontSize}px` } : undefined}
                    >
                        {title}
                    </h1>
                    <h3 
                        ref={instructorRef}
                        className="instructor-name"
                        style={instructorFontSize ? { fontSize: `${instructorFontSize}px` } : undefined}
                    >
                        Instructor: {instructor}
                    </h3>
                </div>
                <img
                    className="instructor-image"
                    src={instructorImage}
                    alt="Instructor"
                />
            </div>
        </div>
    );
};

export default Thumbnail;
